import {
  Component,
  forwardRef,
  Injectable,
  Input,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import {
  OwlDateTimeModule,
  OwlDateTimeIntl,
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
} from '@danielmoncada/angular-datetime-picker';
import { OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import { TranslateService } from '__services';
import * as moment from 'moment';

export const MY_MOMENT_FORMATS = {
  parseInput: 'DD/MM/YYYY HH:mm',
  fullPickerInput: 'DD/MM/YYYY HH:mm',
  datePickerInput: 'DD/MM/YYYY',
  timePickerInput: 'HH:mm',
  monthYearLabel: 'MMM YYYY',
};

@Injectable()
export class DefaultIntl extends OwlDateTimeIntl {
  constructor(private translationService: TranslateService) {
    super();
    this.translationService = translationService;
    this.initLabels();
  }

  private initLabels(): void {
    this.upSecondLabel = this.translationService.translate('Add a second'); // A label for the up second button (used by screen readers).
    this.downSecondLabel = this.translationService.translate('Minus a second'); // A label for the down second button (used by screen readers).
    this.upMinuteLabel = this.translationService.translate('Add a minute'); // A label for the up minute button (used by screen readers).
    this.downMinuteLabel = this.translationService.translate('Minus a minute'); // A label for the down minute button (used by screen readers).
    this.upHourLabel = this.translationService.translate('Add a hour'); // A label for the up hour button (used by screen readers).
    this.downHourLabel = this.translationService.translate('Minus a hour'); // A label for the down hour button (used by screen readers).
    this.prevMonthLabel = this.translationService.translate('Previous month'); // A label for the previous month button (used by screen readers).
    this.nextMonthLabel = this.translationService.translate('Next month'); // A label for the next month button (used by screen readers).
    this.prevYearLabel = this.translationService.translate('Previous year'); // A label for the previous year button (used by screen readers).
    this.nextYearLabel = this.translationService.translate('Next year'); // A label for the next year button (used by screen readers).
    this.prevMultiYearLabel = this.translationService.translate('Previous 21 years'); // A label for the previous multi-year button (used by screen readers).
    this.nextMultiYearLabel = this.translationService.translate('Next 21 years'); // A label for the next multi-year button (used by screen readers).
    this.switchToMonthViewLabel = this.translationService.translate('Change to month view'); // A label for the 'switch to month view' button (used by screen readers).
    this.switchToMultiYearViewLabel = this.translationService.translate('Choose month and year'); // A label for the 'switch to year view' button (used by screen readers).
    this.cancelBtnLabel = this.translationService.translate('Cancel'); // A label for the cancel button
    this.setBtnLabel = this.translationService.translate('Set'); // A label for the set button
    this.rangeFromLabel = this.translationService.translate('From'); // A label for the range 'from' in picker info
    this.rangeToLabel = this.translationService.translate('To'); // A label for the range 'to' in picker info
    this.hour12AMLabel = this.translationService.translate('AM'); // A label for the hour12 button (AM)
    this.hour12PMLabel = this.translationService.translate('PM'); // A label for the hour12 button (PM)
  }
}

@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [OwlDateTimeModule, OwlMomentDateTimeModule, FormsModule],
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
    {
      provide: OwlDateTimeIntl,
      useClass: DefaultIntl,
    },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useValue: MY_MOMENT_FORMATS,
    },
  ],
})
export class DateTimePickerComponent implements ControlValueAccessor {
  constructor(
    private dateTimeAdapter: DateTimeAdapter<any>,
  ) { }

  @Input() placeholder: string = 'Select date and time';
  @Input()
  get defaultValue(): any {
    return this.value;
  }
  set defaultValue(value: any) {
    if (value && value[0]) {
      this.value = value[0];
    } else {
      this.value = value;
    }
  }
  @Input() config: any = {};
  @Input() isDisabled: boolean = false;

  value = null;
  disabled = false;
  minDate = null;
  maxDate = null;

  ///////////////////////////////////

  writeValue(value: any) {
    this.propagateChange = value;
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateChange = fn;
  }

  propagateChange = (_: any) => { };

  ///////////////////////////////////

  onKeydown = (event) => {
    if (event.code === 'Backspace') {
      this.dateTimeChange({ value: null });
    }
  };

  dateTimeChange(data: any) {
    this.value = data.value;
    if (this.config.onChange) {
      this.config.onChange(this.value);
    }
  }

  beforePickerOpen() {
    document.addEventListener('keydown', this.onKeydown)
    if (this.config.onOpen) {
      this.config.onOpen(this.value);
    }
    const locale =
      localStorage.getItem('userLang') == 'es'
        ? 'es-ES'
        : localStorage.getItem('userLang') == 'ja'
          ? 'ja-JP'
          : 'en-US';
    this.dateTimeAdapter.setLocale(locale);
    this.minDate = this.config.minDate ? new Date(this.config.minDate) : null;
    this.maxDate = null;
    if (this.config.maxDate && this.config.maxDate === 'now') {
      this.maxDate = new Date();
    } else if (this.config.maxDate) {
      this.maxDate = new Date(this.config.maxDate);
    }
  }

  afterPickerClosed() {
    document.removeEventListener('keydown', this.onKeydown)
    if (this.config.onClose) {
      this.config.onClose(this.value);
    }
  }

  setDefaultValue(value: any) {
    this.value = value;
  }
}
